// src/components/rpa/agent/BatchProgressCard.js
//
// Renders the result of a chain execution (POST /admin/rpa/agent/chain).
// Shows every step the agent just completed with its AI summary, the overall
// progress bar, and why the chain stopped:
//   requires_file → "Upload X to continue" + button that opens the in-chat uploader
//   complete      → celebration
//   error         → failed step + Resume button (retries from where it stopped)
//   unsupported / max_steps / no_workflow → informative message + Resume where sensible

import React, { useState } from 'react';
import './BatchProgressCard.css';

const STOP_CONFIG = {
  complete:      { icon: '🎉', tone: 'success', title: 'Workflow complete' },
  requires_file: { icon: '📎', tone: 'warn',    title: 'Document needed to continue' },
  error:         { icon: '⛔', tone: 'error',   title: 'Chain stopped on an error' },
  unsupported:   { icon: '🛠️', tone: 'warn',    title: 'Manual step reached' },
  max_steps:     { icon: '⏸️', tone: 'info',    title: 'Chain paused' },
  no_workflow:   { icon: '❓', tone: 'warn',    title: 'No workflow assigned' },
};

const BatchProgressCard = ({ chain, onUploadNeeded, onResume, resuming }) => {
  const [expanded, setExpanded] = useState(() => new Set());

  const { executedSteps = [], stoppedAt = {}, docName, workflow, completed = 0, total = 0 } = chain;
  const stop = STOP_CONFIG[stoppedAt.reason] || STOP_CONFIG.max_steps;
  const pct  = total ? Math.round((completed / total) * 100) : 0;

  const toggle = (stepId) => {
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(stepId) ? next.delete(stepId) : next.add(stepId);
      return next;
    });
  };

  return (
    <div className="batch-card">
      <div className="batch-header">
        <span className="batch-icon">⚡</span>
        <div>
          <div className="batch-title">Chain Execution</div>
          <div className="batch-sub">{docName}{workflow ? ` · ${workflow}` : ''}</div>
        </div>
        <span className="batch-count-badge">
          {executedSteps.filter(s => s.status === 'completed').length} step{executedSteps.filter(s => s.status === 'completed').length !== 1 ? 's' : ''} run
        </span>
      </div>

      {/* Overall progress */}
      <div className="batch-progress-row">
        <div className="batch-progress-bar">
          <div className="batch-progress-fill" style={{ width: `${pct}%` }} />
        </div>
        <span className="batch-progress-label">{completed}/{total} ({pct}%)</span>
      </div>

      {/* Executed steps */}
      {executedSteps.length > 0 && (
        <div className="batch-steps">
          {executedSteps.map((s, i) => {
            const isOpen = expanded.has(s.stepId);
            const failed = s.status === 'failed';
            return (
              <div key={`${s.stepId}-${i}`} className={`batch-step ${failed ? 'failed' : ''}`}>
                <button className="batch-step-header" onClick={() => toggle(s.stepId)}>
                  <span className="batch-step-status">{failed ? '✕' : '✓'}</span>
                  <span className="batch-step-title">{s.stepTitle || s.stepId}</span>
                  {(s.summary || s.error) && (
                    <span className="batch-step-chevron">{isOpen ? '▾' : '▸'}</span>
                  )}
                </button>
                {isOpen && (s.summary || s.error) && (
                  <div className="batch-step-summary">
                    {failed ? s.error : s.summary}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {executedSteps.length === 0 && (
        <div className="batch-no-steps">No AI steps were executable from the current position.</div>
      )}

      {/* Stop reason */}
      <div className={`batch-stop ${stop.tone}`}>
        <span className="batch-stop-icon">{stop.icon}</span>
        <div className="batch-stop-body">
          <div className="batch-stop-title">{stop.title}</div>
          {stoppedAt.message && (
            <div className="batch-stop-message">
              {stoppedAt.message.replace(/\*\*/g, '')}
            </div>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="batch-actions">
        {stoppedAt.reason === 'requires_file' && (
          <button
            className="batch-btn primary"
            onClick={() => onUploadNeeded?.(stoppedAt.stepId, stoppedAt.stepTitle)}
          >
            📎 Upload {stoppedAt.stepTitle?.replace(/^Upload /, '') || 'Document'}
          </button>
        )}
        {(stoppedAt.reason === 'error' || stoppedAt.reason === 'max_steps' || stoppedAt.reason === 'unsupported') && (
          <button className="batch-btn primary" onClick={onResume} disabled={resuming}>
            {resuming ? 'Resuming…' : '▶ Resume Chain'}
          </button>
        )}
      </div>
    </div>
  );
};

export default BatchProgressCard;